import React from 'react'
import { Check, User, Crown, Settings, Mail, Calendar, Clock } from 'lucide-react'

interface User {
  id: string
  name: string
  email?: string
  role: string
  avatar?: string
  receives_daily_reports?: boolean
  receives_weekly_reports?: boolean
}

interface UserSelectionGridProps {
  users: User[]
  onUserReportChange: (userId: string, reportType: 'daily' | 'weekly', enabled: boolean) => void
  onSaveSelections: () => void
  onUserEmailChange?: (userId: string, email: string) => void
}

export const UserSelectionGrid: React.FC<UserSelectionGridProps> = ({
  users,
  onUserReportChange,
  onSaveSelections,
  onUserEmailChange
}) => {
  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin':
        return <Crown size={16} className="text-yellow-500" />
      case 'supervisor':
        return <Settings size={16} className="text-blue-500" />
      default:
        return <User size={16} className="text-gray-500" />
    }
  }

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin':
        return 'Admin'
      case 'supervisor':
        return 'Supervisor'
      case 'operator':
        return 'Operator'
      case 'sales':
        return 'Sales'
      case 'teamleader':
        return 'Teamleader'
      case 'forklift':
        return 'Forklift'
      case 'technical':
        return 'Technical'
      default:
        return 'Gebruiker'
    }
  }

  const getAvatarInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .substring(0, 2)
      .toUpperCase()
  }

  const handleDailyReportToggle = (userId: string, enabled: boolean) => {
    onUserReportChange(userId, 'daily', enabled)
  }

  const handleWeeklyReportToggle = (userId: string, enabled: boolean) => {
    onUserReportChange(userId, 'weekly', enabled)
  }

  const getDailyReportCount = () => {
    return users.filter(user => user.receives_daily_reports === true).length
  }

  const getWeeklyReportCount = () => {
    return users.filter(user => user.receives_weekly_reports === true).length
  }

  if (users.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <User size={48} className="mx-auto mb-4" />
        <p>Geen gebruikers gevonden</p>
      </div>
    )
  }

  return (
    <div>
      {/* Summary Stats */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Calendar size={20} className="text-green-600" />
            <h4 className="font-semibold text-green-800">Dagelijkse Rapporten</h4>
          </div>
          <div className="text-2xl font-bold text-green-600">{getDailyReportCount()}</div>
          <div className="text-sm text-green-600">gebruiker(s) ontvangen dagelijks</div>
        </div>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Clock size={20} className="text-blue-600" />
            <h4 className="font-semibold text-blue-800">Wekelijkse Rapporten</h4>
          </div>
          <div className="text-2xl font-bold text-blue-600">{getWeeklyReportCount()}</div>
          <div className="text-sm text-blue-600">gebruiker(s) ontvangen wekelijks</div>
        </div>
      </div>

      {/* Users Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {users.map((user) => (
          <div
            key={user.id}
            className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-all"
          >
            {/* User Header */}
            <div className="flex items-center gap-3 mb-4">
              {/* User Avatar */}
              <div className="w-12 h-12 rounded-full bg-gray-400 flex items-center justify-center text-white font-bold">
                {user.avatar ? (
                  <img
                    src={user.avatar}
                    alt={user.name}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  getAvatarInitials(user.name)
                )}
              </div>

              {/* User Info */}
              <div className="flex-1">
                <h4 className="font-semibold text-gray-800 text-sm">
                  {user.name}
                </h4>
                {onUserEmailChange ? (
                  <input
                    type="email"
                    value={user.email || ''}
                    onChange={(e) => onUserEmailChange(user.id, e.target.value)}
                    placeholder="email@kvh.nl"
                    className="w-full text-xs p-1 border border-gray-300 rounded mb-1 focus:ring-1 focus:ring-kvh-red focus:border-kvh-red"
                  />
                ) : (
                  <p className="text-xs text-gray-500 mb-1">
                    {user.email || 'Geen email'}
                  </p>
                )}
                
                {/* Role Badge */}
                <div className="flex items-center gap-1">
                  {getRoleIcon(user.role)}
                  <span className="text-xs text-gray-600">
                    {getRoleLabel(user.role)}
                  </span>
                </div>
              </div>
            </div>

            {/* Report Toggles */}
            <div className="space-y-3">
              {/* Daily Reports */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Calendar size={16} className="text-green-600" />
                  <span className="text-sm font-medium text-gray-700">
                    Dagelijkse Rapporten
                  </span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={user.receives_daily_reports === true}
                    onChange={(e) => handleDailyReportToggle(user.id, e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                </label>
              </div>

              {/* Weekly Reports */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <Clock size={16} className="text-blue-600" />
                  <span className="text-sm font-medium text-gray-700">
                    Wekelijkse Rapporten
                  </span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={user.receives_weekly_reports === true}
                    onChange={(e) => handleWeeklyReportToggle(user.id, e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>

            {/* Status Summary */}
            <div className="mt-3 pt-3 border-t border-gray-200">
              <div className="flex items-center justify-center gap-4 text-xs">
                {user.receives_daily_reports === true && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded-full">
                    <Check size={12} />
                    Dagelijks
                  </span>
                )}
                {user.receives_weekly_reports === true && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                    <Check size={12} />
                    Wekelijks
                  </span>
                )}
                {user.receives_daily_reports !== true && user.receives_weekly_reports !== true && (
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                    Geen rapporten
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Save Button */}
      <div className="flex justify-center">
        <button
          onClick={onSaveSelections}
          className="px-6 py-2 bg-kvh-red text-white rounded-lg hover:bg-kvh-dark-red transition-colors flex items-center gap-2"
        >
          <Mail size={16} />
          Rapport Voorkeuren Opslaan
        </button>
      </div>
    </div>
  )
}